import { useInternationalization } from "@progress/kendo-react-intl";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import DateUtility from "../../../utilities/dateUtilities";

type Props = {
  quote: any;
  units: string;
  showInputs?: boolean;
  isRiskTaker: boolean;
  isSplit: boolean;
};

export const AccumulatorQuoteSummary = ({
  quote,
  units,
  showInputs,
  isRiskTaker,
  isSplit = true,
}: Props) => {
  const formatter = useInternationalization();
  const labelStyle = { fontWeight: "bold" };

  const margin = (quote.brokerPrice - quote.fairValuePrice) / quote.maxVolume;

  // Build the summary fields as an array of label/value pairs
  const fields: { label: string; value: React.ReactNode }[] = [];
  if (showInputs) {
    fields.push(
      { label: "Knock-Out Level", value: formatter.formatNumber(quote.knockOutLevel, "c2") },
      { label: "Strike", value: formatter.formatNumber(quote.strike, "c2") },
    );
  }
  fields.push(
    {
      label: "First Fixing Date",
      value: quote.startDate ? DateUtility.formatShortDate(quote.startDate) : "",
    },
    {
      label: "Last Fixing Date",
      value: quote.expiryDate ? DateUtility.formatShortDate(quote.expiryDate) : "",
    },
    {
      label: "Settlement Date",
      value: quote.endDate ? DateUtility.formatShortDate(quote.endDate) : "",
    },
    { label: "Maximum Volume", value: `${formatter.formatNumber(quote.maxVolume, "n0")} ${units}` },
    { label: "Number of Fixings", value: formatter.formatNumber(quote.numFixings, "n0") },
    {
      label: "Volume per Fixing",
      value: `${formatter.formatNumber(quote.volumePerFixing, "n2")} ${units}`,
    },
    { label: "Delta", value: `${formatter.formatNumber(quote.delta, "n2")} ${units}` },
    { label: "Underlying Price", value: formatter.formatNumber(quote.underlyingPrice, "c3") },
    {
      label: "Unit Price",
      value: `${formatter.formatNumber(quote.unitPrice, "c3")} / ${units.slice(0, -1)}`,
    },
  );
  if (isRiskTaker) {
    fields.push({
      label: "Margin",
      value: formatter.formatNumber(margin, "c3"),
    });
  }

  if (isSplit) {
    // Two-column layout using GridLayout
    const mid = Math.ceil(fields.length / 2);
    const left = fields.slice(0, mid);
    const right = fields.slice(mid);
    // Place left and right fields in two columns, same row index
    return (
      <GridLayout gap={{ cols: 100, rows: 10 }}>
        {left.map((f, i) => (
          <GridLayoutItem col={1} row={i + 1} key={`l_${i}`}>
            <span style={labelStyle}>{f.label}</span>: <span>{f.value}</span>
          </GridLayoutItem>
        ))}
        {right.map((f, i) => (
          <GridLayoutItem col={2} row={i + 1} key={`r_${i}`}>
            <span style={labelStyle}>{f.label}</span>: <span>{f.value}</span>
          </GridLayoutItem>
        ))}
      </GridLayout>
    );
  }

  // Default: single column
  let row = 1;
  return (
    <GridLayout align={{ vertical: "bottom" }} gap={{ cols: 10, rows: 10 }}>
      {fields.map((f) => (
        <>
          <GridLayoutItem col={1} row={row} key={`l_${row}`}>
            <span style={labelStyle}>{f.label}</span>
          </GridLayoutItem>
          <GridLayoutItem col={2} row={row++} key={`v_${row}`}>
            {f.value}
          </GridLayoutItem>
        </>
      ))}
    </GridLayout>
  );
};
